import { useState, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scale, Plus, Trash2, ArrowRight, ChevronDown, X, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ALL_JURISDICTIONS, ADMIN_JURISDICTIONS, JURISDICTION_LABELS } from './types';
import type { LegalQuestion, Jurisdiction, FondementJuridique } from './types';

// Liste des juridictions affichées dans le sélecteur du form : judiciaires +
// administratives. Le défaut sélectionné (state initial) reste judiciaire seul —
// l'admin est opt-in côté lawyer. Backend résout l'ordre via `resolveSearchJurisdictions`
// (specialisationRouting.ts) qui priorise la liste explicite si non vide.
const SELECTABLE_JURISDICTIONS: Jurisdiction[] = [...ALL_JURISDICTIONS, ...ADMIN_JURISDICTIONS];

interface JurisprudenceSearchFormProps {
  jobId: string;
  initialQuestions?: LegalQuestion[];
  initialJurisdictions?: Jurisdiction[];
  initialFondements?: FondementJuridique[];
  autoApprove?: boolean;
  onSubmit?: (jobId: string, questions: LegalQuestion[], jurisdictions: Jurisdiction[], fondements: FondementJuridique[]) => void;
  onCancel?: (jobId: string) => void;
  isLoading?: boolean;
  /** Keep a persisted proposal visible when the conversation is no longer editable. */
  isReadOnly?: boolean;
}

const createDefaultQuestion = (): LegalQuestion => ({
  id: crypto.randomUUID(),
  text: '',
  jurisdictions: ['CC', 'CA', 'TJ', 'TCOM'],
});

/**
 * Accepted article number formats:
 * - Numeric: 1240, 1382
 * - L./R./D./A. prefix: L. 442-6, R. 4127-1, D. 311-1, A. 212-1
 * - Hyphenated: L1234-1, L. 1234-1-2
 */


/** Textarea qui se redimensionne automatiquement à la hauteur de son contenu */
function AutoResizeTextarea({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  disabled: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      rows={1}
      className="flex-1 resize-none overflow-hidden text-sm text-grey-800 placeholder:text-grey-400 bg-transparent focus:outline-none leading-relaxed min-w-0"
    />
  );
}

/** Searchable combobox for selecting a Légifrance code */
export function JurisprudenceSearchForm({
  jobId,
  initialQuestions,
  initialJurisdictions,
  initialFondements,
  autoApprove = false,
  onSubmit,
  onCancel,
  isLoading = false,
  isReadOnly = false,
}: JurisprudenceSearchFormProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [questions, setQuestions] = useState<LegalQuestion[]>(
    initialQuestions ?? [createDefaultQuestion(), createDefaultQuestion()],
  );
  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>(
    initialJurisdictions ?? ['CC', 'CA', 'TJ', 'TCOM'],
  );
  const [fondements] = useState<FondementJuridique[]>(
    initialFondements ?? [],
  );

  const updateText = useCallback((id: string, text: string) => {
    setQuestions(prev => prev.map(q => (q.id === id ? { ...q, text } : q)));
  }, []);

  const toggleJurisdiction = useCallback((jur: Jurisdiction) => {
    setJurisdictions(prev => {
      const has = prev.includes(jur);
      if (has && prev.length === 1) return prev;
      return has ? prev.filter(j => j !== jur) : [...prev, jur];
    });
  }, []);

  const addQuestion = useCallback(() => {
    setQuestions(prev => [...prev, createDefaultQuestion()]);
  }, []);

  const removeQuestion = useCallback((id: string) => {
    setQuestions(prev => prev.filter(q => q.id !== id));
  }, []);

  const handleSubmit = () => {
    if (isReadOnly) return;
    const filled = questions.filter(q => q.text.trim());
    if (filled.length > 0) onSubmit?.(jobId, filled, jurisdictions, fondements);
  };

  const canSubmit = !isReadOnly && !isLoading && !!onSubmit && questions.some(q => q.text.trim());

  // Auto-approve: submit immediately without showing the form
  const autoApproveTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoApprove && onSubmit && !isReadOnly && !autoApproveTriggeredRef.current) {
      autoApproveTriggeredRef.current = true;
      const filled = questions.filter(q => q.text.trim());
      if (filled.length > 0) {
        onSubmit(jobId, filled, jurisdictions, fondements);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- guarded by autoApproveTriggeredRef, only needs to fire once on mount
  }, [autoApprove]);

  // Don't render the form if auto-approve
  if (autoApprove && onSubmit && !isReadOnly) return null;

  /** Check if a fondement from the backend has a known code slug */
  return (
    <div className="rounded-lg border border-grey-200 bg-white overflow-hidden">
      {/* Header */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2.5 border-b border-grey-100 bg-grey-50/60 text-left disabled:cursor-not-allowed"
        onClick={() => setIsOpen(o => !o)}
        disabled={isReadOnly}
        aria-expanded={isOpen}
      >
        <Scale className="w-3.5 h-3.5 text-grey-400 flex-shrink-0" />
        <span className="text-xs font-medium text-grey-600">Recherche de jurisprudence</span>
        <ChevronDown
          className={cn(
            'ml-auto w-3 h-3 text-grey-600 flex-shrink-0 transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            {/* ── Questions (top section) ── */}
            <div className="p-3 space-y-2">
              <AnimatePresence initial={false}>
                {questions.map((q, index) => (
                  <motion.div
                    key={q.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    className="group"
                  >
                    <div className="flex items-center gap-3 rounded-lg border border-grey-200 bg-grey-50/40 px-3 py-3 hover:border-grey-200 hover:bg-grey-50 transition-all duration-150">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-grey-900 text-white flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </div>
                      <AutoResizeTextarea
                        value={q.text}
                        onChange={text => updateText(q.id, text)}
                        placeholder={`Question de droit n°${index + 1}…`}
                        disabled={isReadOnly}
                      />
                      {questions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeQuestion(q.id)}
                          disabled={isReadOnly}
                          className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:text-red-500 text-grey-400"
                          title="Supprimer cette question"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              <button
                type="button"
                onClick={addQuestion}
                disabled={isReadOnly}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-dashed border-grey-200 text-xs text-grey-400 hover:text-grey-600 hover:border-grey-400 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Ajouter une question de droit
              </button>
            </div>

            {/* ── Bottom row: Jurisdictions (left) | Fondements (right) ── */}
            <div className="grid grid-cols-2 gap-0 border-t border-grey-100">
              {/* Left: Jurisdictions */}
              <div className="p-3 border-r border-grey-100 space-y-2 flex flex-col items-center justify-center">
                <span className="text-xs font-semibold text-grey-400 uppercase tracking-widest">
                  Juridictions
                </span>
                <div className="flex flex-wrap justify-center items-center gap-1.5">
                  {SELECTABLE_JURISDICTIONS.map(jur => {
                    const selected = jurisdictions.includes(jur);
                    return (
                      <button
                        key={jur}
                        type="button"
                        onClick={() => toggleJurisdiction(jur)}
                        disabled={isReadOnly}
                        className={cn(
                          'px-2.5 py-1.5 rounded-md text-xs font-semibold border transition-all duration-150 select-none',
                          selected
                            ? 'bg-grey-900 text-white border-grey-900 shadow-sm'
                            : 'bg-white text-grey-400 border-grey-200 hover:border-grey-400 hover:text-grey-600',
                        )}
                        title={JURISDICTION_LABELS[jur]}
                      >
                        {jur}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right: Fondements juridiques */}
              <div className="p-3 space-y-2 flex flex-col items-center justify-center">
                <div className="flex items-center gap-1.5">
                  <BookOpen className="w-3 h-3 text-grey-400" />
                  <span className="text-xs font-semibold text-grey-400 uppercase tracking-widest">
                    Fondements juridiques
                  </span>
                </div>

                {/* TODO: rendre éditable quand le backend supportera l'ajout/modif de fondements */}
                <div className="w-full space-y-1.5 max-h-[150px] overflow-y-auto">
                  {fondements.length === 0 ? (
                    <p className="text-xs text-grey-400 italic text-center py-2">
                      Déterminés automatiquement lors de la recherche
                    </p>
                  ) : (
                    <AnimatePresence initial={false}>
                      {fondements.map((f, index) => (
                        <motion.div
                          key={f.key || `fondement-${index}`}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.97 }}
                          transition={{ duration: 0.15, ease: 'easeOut' }}
                        >
                          <div className="flex items-center gap-2 rounded-lg border border-grey-200 bg-grey-50/40 px-2.5 py-2 opacity-75">
                            <span className="text-sm text-grey-600 truncate">
                              {f.codeName}
                            </span>
                            {f.articleNumber && (
                              <span className="text-sm text-grey-600 flex-shrink-0">
                                art. {f.articleNumber}
                              </span>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-grey-100 flex justify-end gap-2">
              {onCancel && (
                <button
                  type="button"
                  onClick={() => {
                    if (!isReadOnly) onCancel(jobId);
                  }}
                  disabled={isLoading || isReadOnly}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-red-500 hover:text-red-700 hover:bg-red-50 border border-red-200 transition-all duration-150"
                >
                  <X className="w-3.5 h-3.5" />
                  Ignorer
                </button>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150',
                  canSubmit
                    ? 'bg-grey-900 text-white hover:bg-grey-800 active:scale-[0.98]'
                    : 'bg-grey-100 text-grey-400 cursor-not-allowed',
                )}
              >
                Lancer la recherche
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
